import { useState } from 'react';
import { Avatar } from '../../Avatar';
import { Drawer, DrawerSection } from '../../billing/Drawer';
import { LastActivity } from '../../accounts/users/LastActivity';
import { useIamUser, useRevokeGrant } from '../../../hooks/useIam';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { CatalogRole, IamGrant } from '../../../services/iam';
import { AddGrantForm } from './AddGrantForm';
import { GrantRow } from './GrantRow';
import { RevokeGrantConfirmModal } from './RevokeGrantConfirmModal';

interface UserDrawerProps {
    userId: string;
    /** Sourced from the loaded list row, not useIamUser. `undefined` means unknown (hides the line); `null` means never signed in. */
    lastActive: string | null | undefined;
    roleLookup: ReadonlyMap<string, CatalogRole>;
    onClose: () => void;
}

// Deep-linked at `/access/users/:userId`, floats over the list like
// ChargesList's ChargeDrawer. Deliberately thin: no effective permission
// list or provenance chains - those live on the Roles/Permissions pages now.
export function UserDrawer({ userId, lastActive, roleLookup, onClose }: UserDrawerProps) {
    const { user, grants, isLoading, isError } = useIamUser(userId);
    const { showToast } = useToast();
    const { hasPermission } = useMyPermissions();
    const canManageAccess = hasPermission('iam.update');
    const { mutateAsync: revoke, isPending: isRevoking } = useRevokeGrant();
    const [showAddGrant, setShowAddGrant] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState<IamGrant | null>(null);

    const handleConfirmRevoke = async () => {
        if (!revokeTarget?.id) return;
        try {
            await revoke({ grantId: revokeTarget.id, userId });
            showToast('Grant revoked', 'success');
            setRevokeTarget(null);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to revoke grant'), 'error');
        }
    };

    if (isLoading || !user) {
        return (
            <Drawer title="User" onClose={onClose}>
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading user…</span>
                </div>
            </Drawer>
        );
    }

    if (isError) {
        return (
            <Drawer title="User" onClose={onClose}>
                <p className="text-sm text-gray-500 py-16 text-center">Couldn't load this user.</p>
            </Drawer>
        );
    }

    return (
        <>
            <Drawer
                leading={
                    <div className="w-10 h-10 shrink-0">
                        <Avatar user={{ name: user.name, email: user.email }} />
                    </div>
                }
                title={user.name || '-'}
                subtitle={
                    <span className="flex items-center gap-1.5">
                        <span className="truncate">{user.email}</span>
                        {lastActive !== undefined && (
                            <>
                                <span className="text-gray-300">·</span>
                                <LastActivity lastActivityAt={lastActive} />
                            </>
                        )}
                    </span>
                }
                onClose={onClose}
                // A stacked overlay owns Escape for the top layer; without this the
                // drawer's own listener fires on the same keypress and closes it too.
                closeDisabled={revokeTarget !== null}
            >
                <DrawerSection
                    title="Grants"
                    action={
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-400">
                                {grants.length} {grants.length === 1 ? 'grant' : 'grants'}
                            </span>
                            {canManageAccess && !showAddGrant && (
                                <button
                                    type="button"
                                    onClick={() => setShowAddGrant(true)}
                                    className="shrink-0 bg-white text-gray-800 border border-gray-300 text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    + Add grant
                                </button>
                            )}
                        </div>
                    }
                >
                    <div className="flex flex-col gap-3">
                        {canManageAccess && showAddGrant && (
                            <AddGrantForm
                                userId={userId}
                                onDone={() => setShowAddGrant(false)}
                                onCancel={() => setShowAddGrant(false)}
                            />
                        )}

                        {grants.length === 0 ? (
                            !showAddGrant && (
                                <p className="text-sm text-gray-400">No grants held.</p>
                            )
                        ) : (
                            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                                {grants.map((grant, index) => (
                                    <GrantRow
                                        key={`${grant.roleKey}-${grant.scope.type}-${grant.scope.id ?? ''}-${index}`}
                                        grant={grant}
                                        roleLookup={roleLookup}
                                        onRevoke={canManageAccess ? setRevokeTarget : undefined}
                                    />
                                ))}
                            </div>
                        )}

                        <p className="text-xs text-gray-500">Grants take effect immediately.</p>
                    </div>
                </DrawerSection>
            </Drawer>

            {revokeTarget && (
                <RevokeGrantConfirmModal
                    grant={revokeTarget}
                    roleLookup={roleLookup}
                    isRevoking={isRevoking}
                    onConfirm={() => {
                        void handleConfirmRevoke();
                    }}
                    onCancel={() => setRevokeTarget(null)}
                />
            )}
        </>
    );
}
